import React, { useEffect, useRef, useState } from 'react';
import { Mail, MapPin } from 'lucide-react';
import { SendEmail } from '../integrations/Core';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) return;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetIdRef.current !== null) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'dark', 
        callback: (token) => {
          setTurnstileToken(token);
          setTurnstileError(null);
        },
        'expired-callback': () => {
          setTurnstileToken('');
        },
        'error-callback': () => {
          setTurnstileToken('');
          setTurnstileError('Captcha verification failed. Please try again.');
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const existingScript = document.querySelector('script[data-turnstile-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', renderWidget);
      return () => existingScript.removeEventListener('load', renderWidget);
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-turnstile-script', 'true');
    script.addEventListener('load', renderWidget);
    document.head.appendChild(script);

    return () => script.removeEventListener('load', renderWidget);
  }, [turnstileSiteKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileSiteKey) {
      setSubmitStatus('error');
      setTurnstileError('Captcha is not configured. Please try again later.');
      return;
    }

    if (!turnstileToken) {
      setSubmitStatus('error');
      setTurnstileError('Please complete the captcha challenge.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setTurnstileError(null);

    try {
      await SendEmail({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        turnstileToken,
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTurnstileToken('');
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
    } catch (error) {
      setSubmitStatus('error');
      if (error?.message?.toLowerCase().includes('captcha')) {
        setTurnstileError('Captcha verification failed. Please try again.');
      }
      if (window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken('');
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <section id="contact" className="scroll-mt-24 space-y-12">
      <div className="lg:hidden text-sm font-bold uppercase tracking-widest text-zinc-200 mb-8 sticky top-0 z-10 w-full bg-zinc-950/90 backdrop-blur pb-4 pt-10">
        Contact
      </div>

      <div className="grid lg:grid-cols-2 gap-16">
        {/* Contact Info */}
        <div className="space-y-8 lg:pr-10">
          <div>
            <h3 className="text-xl font-medium text-zinc-200 mb-8 tracking-wide">Get In Touch</h3>
            <div className="space-y-6 text-sm text-zinc-400">
              <div className="flex items-center gap-4 group">
                <Mail className="w-5 h-5 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                <a href="mailto:contact@arthurpangjiade.dev" className="group-hover:text-zinc-200 transition-colors">contact@arthurpangjiade.dev</a>
              </div>
              <div className="flex items-center gap-4 group">
                <MapPin className="w-5 h-5 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                <span className="group-hover:text-zinc-200 transition-colors">Singapore</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="w-full bg-transparent border-b border-zinc-800 px-0 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="w-full bg-transparent border-b border-zinc-800 px-0 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors"
                />
              </div>
            </div>
            <div>
              <input
                type="text"
                placeholder="Subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                required
                className="w-full bg-transparent border-b border-zinc-800 px-0 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors"
              />
            </div>
            <div>
              <textarea
                placeholder="Message..."
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                required
                rows={4}
                className="w-full bg-transparent border-b border-zinc-800 px-0 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors resize-none"
              />
            </div>

            <div ref={turnstileContainerRef} className="pt-2"></div>

            {turnstileError && (
              <p className="text-sm text-red-500">{turnstileError}</p>
            )}

            {submitStatus === 'success' && (
              <p className="text-sm text-emerald-500">Thank you! Your message has been sent successfully.</p>
            )}

            {submitStatus === 'error' && (
              <p className="text-sm text-red-500">Sorry, there was an error sending your message. Please try again.</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-max bg-zinc-200 hover:bg-white text-zinc-950 px-8 py-3 rounded-full text-sm font-medium tracking-wide transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}